/**
 * Content Studio — the War Room's article command surface.
 *
 * Dead-simple loop: pick app → pick angle → Generate → review → Publish
 * (goes live + SEO instantly, no redeploy) → Share pack (Medium / Reddit /
 * dev.to / X, paste-ready). Styled to match the tradepilot dashboard
 * (light, Inter, violet+teal, soft rounded cards).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { apps as APPS } from "../data/apps";

const FN = "https://us-central1-cosmovex.cloudfunctions.net";
const CATEGORIES = ["AI", "Technology", "Tutorial", "Update"];
const ARCHETYPES = [
  { id: "tutorial", label: "Tutorial", hint: "How-to that uses the app as the tool" },
  { id: "listicle", label: "List / Best-way", hint: "Scannable guide, app as the pick" },
  { id: "promo", label: "Spotlight", hint: "Value-first product feature" },
];

const slugify = (t) =>
  (t || "").toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-")
    .replace(/-+/g, "-").substring(0, 60).replace(/^-+|-+$/g, "");

export default function ContentStudio() {
  const { currentUser } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");      // "generate" | "save" | "pack"
  const [toast, setToast] = useState(null);

  // composer state
  const [appId, setAppId] = useState(APPS[0]?.id || "");
  const [archetype, setArchetype] = useState("tutorial");
  const [angle, setAngle] = useState("");
  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Tutorial");
  const [status, setStatus] = useState("published");
  const [pack, setPack] = useState(null);    // syndication variants

  const app = useMemo(() => APPS.find((a) => a.id === appId), [appId]);

  const flash = (msg, kind = "ok") => { setToast({ msg, kind }); setTimeout(() => setToast(null), 3200); };

  async function loadBlogs() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "Blogs"));
      const list = snap.docs.map((d) => ({
        id: d.id,
        title: d.data().blog_title || "",
        content: d.data().blog_contetnt || d.data().blog_content || "",
        category: d.data().blog_category || "Technology",
        status: d.data().status || "published",
        app_id: d.data().app_id || null,
        createdAt: d.data().createdAt || 0,
      }));
      list.sort((a, b) => b.createdAt - a.createdAt);
      setBlogs(list);
    } catch (e) { flash("Couldn't load library", "err"); }
    setLoading(false);
  }
  useEffect(() => { loadBlogs(); }, []);

  function resetComposer() {
    setEditId(null); setTitle(""); setContent(""); setCategory("Tutorial");
    setStatus("published"); setPack(null); setAngle("");
  }

  async function generate() {
    setBusy("generate"); setPack(null);
    try {
      const r = await fetch(`${FN}/generateAppArticle`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          angle, archetype, category,
          app: app && {
            id: app.id, name: app.name, tagline: app.tagline, description: app.description,
            playstore: app.playstore, category: app.category, features: app.features,
          },
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setTitle(d.title || ""); setContent(d.content || "");
      setCategory(CATEGORIES.includes(d.category) ? d.category : "Tutorial");
      if (d.warnings?.banned_words?.length)
        flash(`Generated — review flagged words: ${d.warnings.banned_words.join(", ")}`, "warn");
      else flash("Article generated — review, then publish");
    } catch (e) { flash("Generation failed — try again", "err"); }
    setBusy("");
  }

  async function save(asStatus) {
    if (!title.trim() || !content.trim()) { flash("Title and content are required", "warn"); return; }
    setBusy("save");
    const st = asStatus || status;
    const payload = {
      blog_title: title, blog_contetnt: content, blog_category: category,
      status: st, app_id: appId || null, archetype, updatedAt: Date.now(),
    };
    try {
      if (editId) {
        await updateDoc(doc(db, "Blogs", editId), payload);
      } else {
        const ref = await addDoc(collection(db, "Blogs"), { ...payload, createdAt: Date.now() });
        setEditId(ref.id);
      }
      setStatus(st);
      flash(st === "published" ? "Published — live + in sitemap now" : "Saved as draft");
      loadBlogs();
    } catch (e) { flash("Save failed", "err"); }
    setBusy("");
  }

  function editBlog(b) {
    setEditId(b.id); setTitle(b.title); setContent(b.content);
    setCategory(b.category); setStatus(b.status);
    if (b.app_id) setAppId(b.app_id);
    setPack(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeBlog(b) {
    if (!window.confirm(`Delete "${b.title}"? This can't be undone.`)) return;
    try {
      await deleteDoc(doc(db, "Blogs", b.id));
      if (editId === b.id) resetComposer();
      flash("Deleted"); loadBlogs();
    } catch (e) { flash("Delete failed", "err"); }
  }

  async function buildPack() {
    if (!content.trim()) { flash("Generate or write content first", "warn"); return; }
    setBusy("pack");
    try {
      const r = await fetch(`${FN}/repurposeArticle`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, content, app: app && { name: app.name, playstore: app.playstore } }),
      });
      if (!r.ok) throw new Error(await r.text());
      setPack(await r.json());
      flash("Share pack ready");
    } catch (e) { flash("Couldn't build share pack", "err"); }
    setBusy("");
  }

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); flash(`${what} copied`); }
    catch { flash("Copy failed — select manually", "err"); }
  };

  // Medium eats raw markdown on paste — open a rendered page so the user copies
  // RICH TEXT (Cmd+A, Cmd+C) and pastes clean into Medium.
  const openMediumPage = (html) => {
    const w = window.open("", "_blank");
    if (!w) { flash("Allow pop-ups to open the Medium page", "warn"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title} — paste into Medium</title>
<style>body{font:20px/1.6 Georgia,serif;max-width:700px;margin:40px auto;padding:0 20px;color:#1a1a1a}
h1{font-size:34px}h2{font-size:28px;margin-top:1.6em}a{color:#1a8917}code{background:#f2f2f2;padding:1px 5px;border-radius:3px;font:0.85em Menlo,monospace}
.b{font:14px -apple-system,sans-serif;color:#b00;border-bottom:1px dashed #ccc;padding-bottom:12px;margin-bottom:24px}</style></head>
<body><p class="b">Title for Medium's title field: <b>${title}</b><br>↓ Select all (Cmd+A), copy (Cmd+C), paste into Medium.</p>${html}</body></html>`);
    w.document.close();
  };

  const liveUrl = title ? `https://cosmovexapps.com/blog/${slugify(title)}` : "";

  return (
    <div className="cs-root">
      <style>{CSS}</style>

      <header className="cs-top">
        <div className="cs-brand"><span className="cs-mark" />Content Studio
          <span className="cs-sub">War Room · articles & syndication</span></div>
        <div className="cs-top-right">
          <a className="cs-ghost" href="/admin">Classic admin</a>
          <span className="cs-user">{currentUser?.email}</span>
        </div>
      </header>

      <div className="cs-grid">
        {/* ───────── Composer ───────── */}
        <section className="cs-card cs-compose">
          <div className="cs-card-h">
            <h2>{editId ? "Edit article" : "New article"}</h2>
            {editId && <button className="cs-link" onClick={resetComposer}>+ Start fresh</button>}
          </div>

          <label className="cs-l">App</label>
          <select className="cs-in" value={appId} onChange={(e) => setAppId(e.target.value)}>
            {APPS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>

          <label className="cs-l">Article type</label>
          <div className="cs-seg">
            {ARCHETYPES.map((a) => (
              <button key={a.id} title={a.hint}
                className={`cs-seg-b ${archetype === a.id ? "on" : ""}`}
                onClick={() => setArchetype(a.id)}>{a.label}</button>
            ))}
          </div>
          <div className="cs-hint">{ARCHETYPES.find((a) => a.id === archetype)?.hint}</div>

          <label className="cs-l">Angle / idea <span className="cs-opt">(optional)</span></label>
          <input className="cs-in" value={angle} onChange={(e) => setAngle(e.target.value)}
            placeholder={`e.g. Practice ${app?.tagline || "this"} without Wi-Fi`} />

          <button className="cs-primary" disabled={busy === "generate"} onClick={generate}>
            {busy === "generate" ? "Generating…" : "✦ Generate article"}
          </button>

          <div className="cs-divider" />

          <label className="cs-l">Title</label>
          <input className="cs-in" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Article title" />

          <label className="cs-l">Content <span className="cs-opt">(markdown)</span></label>
          <textarea className="cs-ta" value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Generated markdown appears here — edit freely." rows={16} />

          <div className="cs-row">
            <div style={{ flex: 1 }}>
              <label className="cs-l">Category</label>
              <select className="cs-in" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="cs-l">Status</label>
              <div className="cs-status">
                <span className={`cs-badge ${status === "published" ? "pub" : "draft"}`}>
                  {status === "published" ? "● Published" : "○ Draft"}
                </span>
              </div>
            </div>
          </div>

          <div className="cs-actions">
            <button className="cs-primary" disabled={busy === "save"} onClick={() => save("published")}>
              {busy === "save" ? "Saving…" : (editId ? "Update & Publish" : "Publish")}
            </button>
            <button className="cs-secondary" disabled={busy === "save"} onClick={() => save("draft")}>Save draft</button>
            <button className="cs-secondary" disabled={busy === "pack" || !content} onClick={buildPack}>
              {busy === "pack" ? "Building…" : "↗ Share pack"}
            </button>
          </div>
          {editId && status === "published" && liveUrl &&
            <a className="cs-live" href={liveUrl} target="_blank" rel="noopener noreferrer">View live → {liveUrl}</a>}
        </section>

        {/* ───────── Library ───────── */}
        <section className="cs-card cs-library">
          <div className="cs-card-h"><h2>Library</h2><span className="cs-count">{blogs.length}</span></div>
          {loading ? <div className="cs-empty">Loading…</div> :
            blogs.length === 0 ? <div className="cs-empty">No articles yet. Generate your first one →</div> :
              <div className="cs-list">
                {blogs.map((b) => (
                  <div key={b.id} className={`cs-item ${editId === b.id ? "sel" : ""}`}>
                    <div className="cs-item-main" onClick={() => editBlog(b)}>
                      <div className="cs-item-t">{b.title || "Untitled"}</div>
                      <div className="cs-item-m">
                        <span className={`cs-badge sm ${b.status === "published" ? "pub" : "draft"}`}>
                          {b.status === "published" ? "Published" : "Draft"}</span>
                        <span className="cs-tag">{b.category}</span>
                        {b.app_id && <span className="cs-tag app">{b.app_id}</span>}
                      </div>
                    </div>
                    <div className="cs-item-acts">
                      <button className="cs-icon" title="Edit" onClick={() => editBlog(b)}>✎</button>
                      <button className="cs-icon del" title="Delete" onClick={() => removeBlog(b)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>}
        </section>
      </div>

      {/* ───────── Syndication pack ───────── */}
      {pack && (
        <section className="cs-card cs-pack">
          <div className="cs-card-h"><h2>Share pack</h2>
            <span className="cs-hint">paste-ready for each channel</span></div>
          <div className="cs-channels">
            <Channel name="Medium" note="opens a rich page — Cmd+A, Cmd+C, paste"
              action={<button className="cs-secondary" onClick={() => openMediumPage(pack.medium_html)}>Open paste-ready page</button>} />
            <Channel name="Reddit" note="raw markdown — paste in the post body"
              action={<button className="cs-secondary" onClick={() => copy(pack.reddit_md, "Reddit markdown")}>Copy markdown</button>}
              body={pack.reddit_md} />
            <Channel name="dev.to" note="markdown with H1 title"
              action={<button className="cs-secondary" onClick={() => copy(pack.devto_md, "dev.to markdown")}>Copy markdown</button>}
              body={pack.devto_md} />
            <Channel name="X / Twitter" note="5-tweet thread"
              action={<button className="cs-secondary" onClick={() => copy(pack.x_thread, "X thread")}>Copy thread</button>}
              body={pack.x_thread} />
          </div>
        </section>
      )}

      {toast && <div className={`cs-toast ${toast.kind}`}>{toast.msg}</div>}
    </div>
  );
}

function Channel({ name, note, action, body }) {
  return (
    <div className="cs-ch">
      <div className="cs-ch-h"><b>{name}</b>{action}</div>
      <div className="cs-ch-note">{note}</div>
      {body !== undefined && <pre className="cs-ch-body">{body}</pre>}
    </div>
  );
}

const CSS = `
.cs-root{--bg:#FBFAF6;--surface:#fff;--border:#E1DCCE;--ink:#26313B;--ink2:#3D4B59;--muted:#5B6B7C;
  --violet:#0F6BBF;--teal:#2E7D4F;--up:#2E7D4F;--down:#C24E4E;--amber:#A8720E;
  --shadow:0 1px 2px rgba(52,44,26,.06),0 4px 14px rgba(52,44,26,.05);
  min-height:100vh;background:var(--bg);
  color:var(--ink);font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;padding:0 0 60px}
.cs-top{display:flex;align-items:center;justify-content:space-between;padding:16px 28px;background:var(--surface);
  border-bottom:1px solid var(--border);position:sticky;top:0;z-index:5}
.cs-brand{display:flex;align-items:center;gap:10px;font-weight:800;font-size:17px}
.cs-mark{width:28px;height:28px;border-radius:8px;background:#E8B44C;border:1px solid #D9A32E}
.cs-sub{font-weight:600;font-size:12px;color:var(--muted);margin-left:6px}
.cs-top-right{display:flex;align-items:center;gap:14px}
.cs-user{font-size:12px;color:var(--muted)}
.cs-ghost{font-size:12px;color:var(--violet);text-decoration:none;border:1px solid rgba(15,107,191,.3);padding:5px 10px;border-radius:8px}
.cs-grid{display:grid;grid-template-columns:1.3fr 1fr;gap:18px;padding:22px 28px;align-items:start}
@media(max-width:900px){.cs-grid{grid-template-columns:1fr}}
.cs-card{background:var(--surface);border:1px solid var(--border);border-radius:16px;box-shadow:var(--shadow);padding:18px}
.cs-card-h{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
.cs-card-h h2{font-size:15px;margin:0;font-weight:800}
.cs-l{display:block;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:var(--muted);margin:12px 0 5px}
.cs-opt{font-weight:500;text-transform:none;letter-spacing:0;color:var(--muted)}
.cs-in,.cs-ta{width:100%;box-sizing:border-box;border:1px solid var(--border);border-radius:10px;padding:10px 12px;
  font-size:14px;color:var(--ink);background:#fff;font-family:inherit;outline:none}
.cs-in:focus,.cs-ta:focus{border-color:rgba(217,163,46,.7);box-shadow:0 0 0 3px rgba(232,180,76,.18)}
.cs-ta{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13px;line-height:1.55;resize:vertical}
.cs-seg{display:flex;gap:6px}
.cs-seg-b{flex:1;border:1px solid var(--border);background:#fff;border-radius:10px;padding:9px;font-size:13px;
  font-weight:600;color:var(--ink2);cursor:pointer;transition:.12s}
.cs-seg-b.on{background:rgba(232,180,76,.16);border-color:#D9A32E;color:#A8720E}
.cs-hint{font-size:11.5px;color:var(--muted);margin-top:6px}
.cs-primary{background:#E8B44C;color:#221A06;border:1px solid #D9A32E;border-radius:10px;
  padding:11px 16px;font-size:14px;font-weight:700;cursor:pointer;width:100%;margin-top:14px}
.cs-primary:disabled{opacity:.55;cursor:default}
.cs-secondary{background:#fff;color:var(--ink2);border:1px solid var(--border);border-radius:10px;padding:10px 14px;
  font-size:13px;font-weight:600;cursor:pointer}
.cs-secondary:disabled{opacity:.5}
.cs-divider{height:1px;background:var(--border);margin:18px 0}
.cs-row{display:flex;gap:12px}
.cs-status{display:flex;align-items:center;height:40px}
.cs-badge{font-size:11px;font-weight:700;padding:4px 10px;border-radius:7px}
.cs-badge.pub{background:rgba(18,183,106,.12);color:var(--up)}
.cs-badge.draft{background:rgba(168,114,14,.14);color:#A8720E}
.cs-badge.sm{font-size:10px;padding:2px 7px}
.cs-actions{display:flex;gap:8px;margin-top:16px;flex-wrap:wrap}
.cs-actions .cs-primary{width:auto;flex:1;margin-top:0}
.cs-live{display:inline-block;margin-top:12px;font-size:12px;color:var(--violet);text-decoration:none}
.cs-link{background:none;border:none;color:var(--violet);font-size:12px;font-weight:600;cursor:pointer}
.cs-count{font-size:12px;font-weight:700;color:var(--muted);background:#F1EEE5;border-radius:7px;padding:2px 9px}
.cs-empty{font-size:13px;color:var(--muted);padding:24px 0;text-align:center}
.cs-list{display:flex;flex-direction:column;gap:8px;max-height:70vh;overflow:auto}
.cs-item{display:flex;align-items:center;border:1px solid var(--border);border-radius:11px;padding:10px 12px;transition:.12s}
.cs-item:hover{border-color:rgba(217,163,46,.55)}
.cs-item.sel{background:rgba(232,180,76,.12);border-color:#D9A32E}
.cs-item-main{flex:1;cursor:pointer;min-width:0}
.cs-item-t{font-size:13.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cs-item-m{display:flex;gap:6px;margin-top:5px;align-items:center}
.cs-tag{font-size:10px;font-weight:600;color:var(--muted);background:#F1EEE5;border-radius:5px;padding:2px 7px}
.cs-tag.app{color:#A8720E;background:rgba(168,114,14,.1)}
.cs-item-acts{display:flex;gap:4px;margin-left:8px}
.cs-icon{border:none;background:#F1EEE5;border-radius:8px;width:30px;height:30px;cursor:pointer;font-size:13px}
.cs-icon.del:hover{background:rgba(194,78,78,.12)}
.cs-pack{margin:0 28px}
.cs-channels{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media(max-width:900px){.cs-channels{grid-template-columns:1fr}}
.cs-ch{border:1px solid var(--border);border-radius:12px;padding:13px;background:#fff}
.cs-ch-h{display:flex;align-items:center;justify-content:space-between}
.cs-ch-h b{font-size:14px}
.cs-ch-note{font-size:11px;color:var(--muted);margin:5px 0 8px}
.cs-ch-body{font-size:11.5px;font-family:ui-monospace,Menlo,monospace;background:#F1EEE5;border:1px solid var(--border);
  border-radius:8px;padding:10px;max-height:200px;overflow:auto;white-space:pre-wrap;color:var(--ink);margin:0}
.cs-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#26313B;color:#fff;
  padding:11px 18px;border-radius:11px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(52,44,26,.2);z-index:50}
.cs-toast.err{background:var(--down)}.cs-toast.warn{background:#E8B44C;color:#221A06}
`;
